import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/system';
import InfoIcon from '@mui/icons-material/Info';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import CountdownTimer from 'pages/NewDashboard/components/Countdown';
import CountDownText from 'pages/NewDashboard/components/CountdownText';
import TransactionHashesModal from 'pages/NewDashboard/components/TxHashes';

import {
	getRewardStatus,
	hasCreationWithinWindow,
	isDateWithinClaimWindow,
	isWithinClaimWindow,
} from 'utils/claim';
import { isPassed } from 'utils/lockDuration';

import {
	createNewClaimReward,
	getClaimWindow,
	getUserDeposit,
	listOfAllRewardRequest,
	UserWithdrawDeposit,
} from 'apis';
import {
	AGENT,
	CLAIM_REWARD_LOCK_TIME,
	REWARD_COLOR,
	TOKEN,
} from 'data/constant';

import { RootState } from 'store';
import { ClaimRewardRequest, ClaimWindow } from 'types/claim';
import { AccountType, TOKEN_NAME } from 'types/distribution';
import { useAptosWalletContext } from 'context/aptosProvider';

const Row = styled('div')`
	display: flex;
	flex-direction: row;
`;
const Column = styled('div')`
	display: flex;
	flex-direction: column;
	height: 100%;
`;
const ContainerRow = styled('div')`
	display: flex;
	min-height: 60px;
	width: 100%;
	height: 100%;
`;

const PrimaryButton = styled(Button)(() => ({
	backgroundColor: '#345CF8',
	width: '100%',
	whiteSpace: 'nowrap',
	color: '#FFFFFF',
	padding: '12px 10px',
	fontFamily: 'AtAmiga',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		backgroundColor: '#2a4be0',
		boxShadow: 'none',
	},
	'&:disabled': {
		backgroundColor: '#748cf6',
		boxShadow: 'none',
	},
}));

const CathenaBox = styled('div')`
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	background-color: #3fe4ee;
	width: 100%;
	position: absolute;
	bottom: 0.7px;
	left: 0.7px;
	padding: 4px 10px;
`;

const TextBox = styled('div')<{ color: string }>`
	display: flex;
	align-items: center;
	padding: 6px 10px;
	background-color: ${({ color }) => `${color}22`};
	border: ${({ color }) => `1px solid ${color}`};
	border-radius: 4px;
	height: 35px;
	font-size: 14px;
`;

const RewardItem = ({
	tokenName,
	amount,
}: {
	tokenName: string;
	amount: number;
}) => (
	<Row
		style={{
			justifyContent: 'space-between',
			marginBottom: 9,
			borderRadius: 4,
		}}
	>
		<Typography variant="caption">{tokenName}</Typography>
		<Row style={{ alignItems: 'center' }}>
			<img
				src={TOKEN[tokenName as TOKEN_NAME]?.url}
				width={18}
				height={18}
				alt={tokenName}
			/>
			<Box width={5} />
			<Typography variant="caption">
				{parseFloat(`${amount}`).toFixed(3)}
			</Typography>
		</Row>
	</Row>
);

export const RewardClaim = ({
	claims,
	accountInformation,
	contractBal,
	onSuccess,
}: {
	claims: UserWithdrawDeposit[];
	accountInformation: AccountType | null;
	contractBal: string;
	onSuccess?(): void;
}) => {
	const [listOfAllRewards, setListOfAllRewards] = useState<
		ClaimRewardRequest[]
	>([]);
	const [loading, setLoading] = useState(false);
	const [txOpen, setTxOpen] = useState(false);
	const [claimWindow, setClaimWindow] = useState<ClaimWindow>({
		windowStart: 0,
		windowEnd: 0,
		nextWindowStart: 0,
		nextWindowEnd: 0,
	});
	const [userDeposits, setUserDeposits] = useState<{
		lastDeposit: Required<UserWithdrawDeposit>[];
	}>({ lastDeposit: [] });
	const [pollingInterval, setPollingInterval] = useState<number | null>(null);
	const [pollingAttempts, setPollingAttempts] = useState(0);

	const vault = useSelector((state: RootState) => state.vault);
	const selectedContract = vault.exchanger.find(
		(d) => d.value === vault.exchange,
	);

	const selectedExchange = vault.exchanger.find(
		(d) => d.value === vault.exchange,
	);
	const selectedAsset = vault.token.find(
		(d) => d.name === vault.selectedToken,
	);
	const { account, isConnected } = useAptosWalletContext();
	// const { address: account, isConnected } = useAccount();
	const claimStatus = getRewardStatus(claimWindow);

	const isClaim = useMemo(
		() =>
			claims.filter(
				(f) =>
					f.eventType === 'claim' && f.claimStatus === 'processing',
			),
		[claims],
	);
	const isWithdraw = useMemo(
		() =>
			claims.filter(
				(f) =>
					f.eventType === 'withdraw' && f.claimStatus === 'processed',
			),
		[claims],
	);

	const isClaimRewardLock = useMemo(() => {
		const last = userDeposits.lastDeposit.at(-1);
		return last && userDeposits.lastDeposit.length > 0
			? isPassed(last.createdAt, CLAIM_REWARD_LOCK_TIME).isLocked
			: false;
	}, [userDeposits]);

	const payload = useMemo(
		() => ({
			contract: selectedContract?.depositContractAddress || '',
			address: account || '',
		}),
		[account, selectedContract?.depositContractAddress],
	);

	const startPolling = useCallback(() => {
		if (pollingInterval === null) {
			setPollingInterval(5000);
			setPollingAttempts(0);
		}
	}, [pollingInterval]);

	const fetchListOfAllReward = useCallback(() => {
		return listOfAllRewardRequest({
			domain: window.location.host,
			address: account || '',
			contract: selectedExchange?.depositContractAddress || '',
		}).then((rewards) => {
			setListOfAllRewards(rewards);
			return rewards;
		});
	}, [account, selectedExchange?.depositContractAddress]);

	const fetchData = useCallback(() => {
		setLoading(true);
		getClaimWindow(AGENT)
			.then(setClaimWindow)
			.finally(() => setLoading(false));
		getUserDeposit(payload).then(setUserDeposits);
		fetchListOfAllReward();
	}, [payload, fetchListOfAllReward]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	useEffect(() => {
		const latestReward = listOfAllRewards.find(
			(r) => r.eventType === 'reward',
		);
		if (
			latestReward &&
			!['processed', 'rejected'].includes(latestReward.rewardStatus || '')
		) {
			startPolling();
		}
	}, [listOfAllRewards, startPolling]);

	useEffect(() => {
		let intervalId: NodeJS.Timeout;

		if (pollingInterval !== null) {
			intervalId = setInterval(() => {
				fetchListOfAllReward().then((rewards) => {
					const latestReward = rewards.find(
						(r) => r.eventType === 'reward',
					);
					if (
						latestReward &&
						(latestReward.rewardStatus === 'processed' ||
							latestReward.rewardStatus === 'rejected')
					) {
						clearInterval(intervalId);
						setPollingInterval(null);
						setPollingAttempts(0);
						onSuccess?.();
					} else {
						setPollingAttempts((prev) => prev + 1);
						if (pollingAttempts >= 6) {
							setPollingInterval(10000);
						} else if (pollingAttempts >= 12) {
							setPollingInterval(30000);
						} else if (pollingAttempts >= 15) {
							setPollingInterval(60000);
						}
					}
				});
			}, pollingInterval);
		}

		return () => {
			if (intervalId) clearInterval(intervalId);
		};
	}, [pollingInterval, pollingAttempts, fetchListOfAllReward]);

	const createRewardClaim = async () => {
		if (!claimStatus.isOpen)
			return toast(claimStatus.text, { type: 'error', autoClose: false });
		if (isClaim.length || isWithdraw.length)
			return toast('Reward claimed already', { type: 'error' });
		if (hasCreationWithinWindow(listOfAllRewards, claimWindow)) {
			return toast('Reward claim in process...', { type: 'error' });
		}
		if (userDeposits.lastDeposit.length === 0) {
			return toast('Deposit now to start earning rewards', {
				type: 'info',
			});
		}
		try {
			setLoading(true);
			await createNewClaimReward({
				domain: window.location.host,
				address: account || '',
				contract: selectedContract?.depositContractAddress || '',
				agent: AGENT,
				token: selectedAsset?.name,
			});
			toast('Reward claim is registered', { type: 'success' });
			startPolling();
			fetchData();
		} catch (e) {
			toast('Reward claim failed', { type: 'error' });
		} finally {
			setLoading(false);
		}
	};

	const isRewardProcess = useMemo(() => {
		const reward = listOfAllRewards.find((r) => r.eventType === 'reward');
		return reward
			? { status: reward.rewardStatus, exists: true, data: reward }
			: {
					status: null,
					exists: false,
			  };
	}, [listOfAllRewards]);

	const getTotalTokenAmount = useMemo((): number => {
		const tokens = accountInformation?.tokens ?? [];
		return tokens.reduce((sum, token) => sum + token.amount, 0);
	}, [accountInformation]);

	const latestReward = useMemo(() => {
		if (!listOfAllRewards?.length) return null;
		if (listOfAllRewards.length === 1) return listOfAllRewards[0];
		return listOfAllRewards.reduce((latest, current) =>
			new Date(current?.createdAt || '') >
			new Date(latest?.createdAt || '')
				? current
				: latest,
		);
	}, [listOfAllRewards]);

	const NextTimer = () => (
		<>
			<Typography variant={'caption'} fontWeight={600}>
				{'Reward claim open in'}
			</Typography>
			<Box height={10} />
			<CountdownTimer
				size="medium"
				targetDate={
					isWithinClaimWindow(claimWindow)
						? claimWindow.nextWindowStart
						: claimWindow.windowStart
				}
			/>
		</>
	);

	const WithInTimer = () => (
		<Column>
			{!isRewardProcess.exists && account && (
				<>
					<Box height={30} />
					<CathenaBox>
						<Typography
							variant="subtitle2"
							color="#fff"
							fontWeight={600}
						>
							CLAIM WITHIN
						</Typography>
						<CountDownText targetDate={claimWindow.windowEnd} />
					</CathenaBox>
				</>
			)}
		</Column>
	);

	const Component = useMemo(() => {
		{
			/* TODO:// fix this later */
		}
		if (true || isWithinClaimWindow(claimWindow)) {
			if (false && isConnected) {
				if (parseFloat(contractBal) > 0) {
					if (accountInformation && getTotalTokenAmount > 0) {
						return (
							<ContainerRow>
								<Column style={{ width: '100%' }}>
									<Row
										style={{
											width: '100%',
											justifyContent: 'space-between',
										}}
									>
										<Column>
											<Typography
												variant="subtitle2"
												color="#000"
												fontWeight={600}
											>
												Rewards available
											</Typography>
											<Box height={8} />
											{accountInformation?.tokens?.map(
												(d, i) => (
													<RewardItem
														key={i}
														tokenName={d.tokenName}
														amount={d.amount}
													/>
												),
											)}
										</Column>
										{!isRewardProcess.exists && (
											<PrimaryButton
												onClick={createRewardClaim}
												disabled={
													loading ||
													!account ||
													isClaimRewardLock
												}
												sx={{
													height: 42,
													width: 'min-content',
												}}
											>
												<Typography
													variant="button"
													fontWeight="bold"
													py="6px"
													color="#fff"
													style={{
														lineHeight: '120%',
													}}
												>
													CLAIM REWARD
												</Typography>
											</PrimaryButton>
										)}
										{isRewardProcess.exists && (
											<TextBox
												color={
													REWARD_COLOR[
														isRewardProcess.status ||
															'registered'
													]
												}
												onClick={() => {
													if (
														isRewardProcess.data
															?.rewardTxs
															?.length ||
														isRewardProcess.data
															?.rewardRejectionReason
													) {
														setTxOpen(true);
													}
												}}
												style={{ cursor: 'pointer' }}
											>
												<Typography
													variant="caption"
													fontWeight="bold"
													color={
														REWARD_COLOR[
															isRewardProcess.status ||
																'registered'
														]
													}
													fontFamily="Roboto Mono"
												>
													{isRewardProcess.status?.toUpperCase()}
												</Typography>
												<Box width={5} />
												<InfoIcon
													style={{
														width: 20,
														height: 20,
														color: REWARD_COLOR[
															isRewardProcess.status ||
																'registered'
														],
													}}
												/>
											</TextBox>
										)}
									</Row>
									<WithInTimer />
								</Column>
							</ContainerRow>
						);
					} else if (
						latestReward &&
						isDateWithinClaimWindow(
							claimWindow,
							latestReward?.createdAt,
						)
					) {
						return (
							<ContainerRow>
								<Column style={{ width: '100%' }}>
									<Row
										style={{
											width: '100%',
											justifyContent: 'space-between',
										}}
									>
										<Column>
											<Typography
												variant="subtitle2"
												color="#000"
												fontWeight={600}
											>
												Reward Claim Status
											</Typography>
											<Box height={8} />
											{accountInformation?.tokens?.map(
												(d, i) =>
													d.amount !== 0 && (
														<RewardItem
															key={i}
															tokenName={
																d.tokenName
															}
															amount={d.amount}
														/>
													),
											)}
										</Column>
										{!isRewardProcess.exists && (
											<PrimaryButton
												onClick={createRewardClaim}
												disabled={
													loading ||
													!account ||
													isClaimRewardLock
												}
												sx={{
													height: 42,
													width: 'min-content',
												}}
											>
												<Typography
													variant="button"
													fontWeight="bold"
													py="6px"
													color="#fff"
													style={{
														lineHeight: '120%',
													}}
												>
													CLAIM REWARD
												</Typography>
											</PrimaryButton>
										)}
										{isRewardProcess.exists && (
											<TextBox
												color={
													REWARD_COLOR[
														isRewardProcess.status ||
															'registered'
													]
												}
												onClick={() => {
													if (
														isRewardProcess.data
															?.rewardTxs
															?.length ||
														isRewardProcess.data
															?.rewardRejectionReason
													) {
														setTxOpen(true);
													}
												}}
												style={{ cursor: 'pointer' }}
											>
												<Typography
													variant="caption"
													fontWeight="bold"
													color={
														REWARD_COLOR[
															isRewardProcess.status ||
																'registered'
														]
													}
													fontFamily="Roboto Mono"
												>
													{isRewardProcess.status?.toUpperCase()}
												</Typography>
												<Box width={5} />
												<InfoIcon
													style={{
														width: 20,
														height: 20,
														color: REWARD_COLOR[
															isRewardProcess.status ||
																'registered'
														],
													}}
												/>
											</TextBox>
										)}
									</Row>
									{/*<WithInTimer />*/}
								</Column>
							</ContainerRow>
						);
					}
					return <NextTimer />;
				}
				return (
					<Row style={{ justifyContent: 'start', width: '100%' }}>
						<img
							src={vault.token[0].icon}
							height={24}
							width={24}
							alt="USDC"
						/>
						<Box width={10} />
						<Typography variant="subtitle2" fontWeight={500}>
							Deposit {vault.token[0].name}, start earning rewards
						</Typography>
					</Row>
				);
			}
			return (
				<Row style={{ justifyContent: 'start', width: '100%' }}>
					<img
						src={vault.token[0].icon}
						height={24}
						width={24}
						alt="USDC"
					/>
					<Box width={10} />
					<Typography variant="subtitle2" fontWeight={500}>
						Deposit {vault.token[0].name}, start earning rewards
					</Typography>
				</Row>
			);
		}
		return <NextTimer />;
	}, [
		claimWindow,
		isConnected,
		vault.token,
		contractBal,
		accountInformation,
		getTotalTokenAmount,
		latestReward,
		isRewardProcess.exists,
		isRewardProcess.status,
		isRewardProcess.data?.rewardTxs?.length,
		isRewardProcess.data?.rewardRejectionReason,
		createRewardClaim,
		loading,
		account,
		isClaimRewardLock,
	]);

	return (
		<>
			{Component}
			<TransactionHashesModal
				open={txOpen}
				onClose={() => setTxOpen(false)}
				txHashes={isRewardProcess?.data?.rewardTxs || []}
				reasonOfRejection={
					isRewardProcess?.data?.rewardRejectionReason || ''
				}
			/>
		</>
	);
};
